import type {
  InspectionTemplate,
  TemplateItem,
  TemplateSection,
} from 'inspection-types';

export function cloneTemplateSection(section: TemplateSection): TemplateSection {
  return {
    ...section,
    items: section.items.map(item => ({ ...item })),
  };
}

/**
 * When a blueprint baseline exists, the persisted draft template’s item count for a
 * baseline-backed section is the source of truth for “X of Y items included”.
 * Overlay-only counts can drift from the server after refetch and show “N of N”.
 *
 * @returns `undefined` when this section is not on the baseline (use merged rows +
 * overlay ids), otherwise the draft’s current item count for the section (0 if absent).
 */
export function baselineBackedCustomizeSectionIncludedCount(
  sectionId: string,
  baselineTemplate: InspectionTemplate | undefined,
  draftTemplate: InspectionTemplate | undefined
): number | undefined {
  if (!baselineTemplate?.sections.some(s => s.id === sectionId)) {
    return undefined;
  }

  return draftTemplate?.sections.find(s => s.id === sectionId)?.items.length ?? 0;
}

export function mergeRemovedSectionSnapshotsIntoOrderedSections(
  serverSections: TemplateSection[],
  removedSectionSnapshots: Record<string, TemplateSection>
): TemplateSection[] {
  const serverIds = new Set(serverSections.map(s => s.id));
  const extras = Object.values(removedSectionSnapshots).filter(
    s => !serverIds.has(s.id)
  );
  return [...serverSections, ...extras].sort((a, b) => a.order - b.order);
}

export function countSectionsAndQuestions(template: InspectionTemplate): {
  sections: number;
  questions: number;
} {
  const sections = template.sections.length;
  const questions = template.sections.reduce(
    (total, section) => total + section.items.length,
    0
  );
  return { sections, questions };
}

export function formatQuestionOrdinal(
  sectionIndex: number,
  itemOrdinalSegmentIndex: number
): string {
  return `${sectionIndex + 1}.${itemOrdinalSegmentIndex + 1}`;
}

/**
 * 0-based second segment for customize row labels ({@link formatQuestionOrdinal}).
 *
 * - **Active** rows: counts only preceding active rows so labels stay contiguous after
 *   a middle removal (former 1.3 becomes 1.2 behind a removed-overlay row).
 * - **Removed overlay** rows: ordinal matches their merged list index so labels stay where
 *   the removed question was (often same number as the next active row when reordering UX).
 */
export function computeCustomizeItemOrdinalIndex(
  items: TemplateItem[],
  index: number,
  removedOverlayItemIds: ReadonlySet<string>
): number {
  const rowId = items[index]?.id;
  if (rowId == null) {
    return index;
  }

  if (removedOverlayItemIds.has(rowId)) {
    return index;
  }

  let activeBeforeThisRow = 0;
  for (let i = 0; i < index; i++) {
    if (!removedOverlayItemIds.has(items[i].id)) {
      activeBeforeThisRow += 1;
    }
  }

  return activeBeforeThisRow;
}

/** Section title prefix for accordion and sidebar (e.g. "1. Interior"). */
export function formatSectionTitle(sectionIndex: number, name: string): string {
  return `${sectionIndex + 1}. ${name}`;
}

/**
 * Client-side projection after dropping an item and bumping aggregate version.
 * For optimistic UI where the mutation implies the layout; callers that need authoritative
 * data should refetch (e.g. `currentInspectionTemplate`).
 */
export function deriveInspectionTemplateAfterItemRemoved(
  current: InspectionTemplate,
  itemId: string,
  newAggregateVersion: number
): InspectionTemplate {
  return {
    ...current,
    version: newAggregateVersion,
    sections: current.sections.map(section => ({
      ...section,
      items: section.items.filter(item => item.id !== itemId),
    })),
  };
}

export function deriveInspectionTemplateAfterSectionRemoved(
  current: InspectionTemplate,
  sectionId: string,
  newAggregateVersion: number
): InspectionTemplate {
  return {
    ...current,
    version: newAggregateVersion,
    sections: current.sections.filter(section => section.id !== sectionId),
  };
}
